import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Folder, Hash, Lock, RefreshCw, Save, X } from 'lucide-react';
import { useServerStore } from '../../store/serverStore';
import { Permissions } from '../../lib/permissions';

interface EditChannelModalProps {
  channel: { id: string; name: string; description?: string | null; type: string; parent_id?: string | null; permission_sync_state?: string | null };
  onClose: () => void;
}

const PERMS = [
  { flag: Permissions.VIEW_CHANNELS, label: 'View Channels' },
  { flag: Permissions.SEND_MESSAGES, label: 'Send Messages' },
  { flag: Permissions.ATTACH_FILES, label: 'Attach Files' },
  { flag: Permissions.ADD_REACTIONS, label: 'Add Reactions' },
  { flag: Permissions.MANAGE_MESSAGES, label: 'Manage Messages' },
  { flag: Permissions.CONNECT_VOICE, label: 'Connect' },
  { flag: Permissions.MANAGE_CHANNELS, label: 'Manage Channels' },
  { flag: Permissions.MANAGE_ROLES, label: 'Manage Roles' },
];

const EditChannelModal: React.FC<EditChannelModalProps> = ({ channel, onClose }) => {
  const { currentServer, roles, members, syncChannelPermissions, fetchServerChannels, fetchServerRoles, fetchServerMembers } = useServerStore();
  const [tab, setTab] = useState<'overview' | 'permissions'>('overview');
  const [name, setName] = useState(channel.name);
  const [description, setDescription] = useState(channel.description || '');
  const [overwrites, setOverwrites] = useState<any[]>([]);
  const [selectedTarget, setSelectedTarget] = useState<{ type: 'role' | 'member'; id: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('access_token');

  useEffect(() => {
    fetchServerRoles(currentServer?.id || '');
    fetchServerMembers(currentServer?.id || '');
    axios
      .get(`${import.meta.env.VITE_API_URL}/channels/${channel.id}/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setOverwrites(Array.isArray(res.data) ? res.data : []))
      .catch(() => setOverwrites([]));
  }, [channel.id, currentServer?.id]);

  const targets = useMemo(() => {
    const roleTargets = roles.map((role: any) => ({
      type: 'role' as const,
      id: role.id,
      label: role.name,
      color: role.color,
      isEveryone: role.is_everyone,
    }));
    const memberTargets = members.map((member: any) => ({
      type: 'member' as const,
      id: member.user_id,
      label: member.user?.display_name || member.user?.username || 'Unknown User',
      bot: Boolean(member.bot || member.user?.bot || member.user?.is_system),
    }));
    return [...roleTargets, ...memberTargets];
  }, [roles, members]);

  useEffect(() => {
    if (!selectedTarget && targets.length > 0) {
      const everyone = targets.find((target) => target.type === 'role' && target.isEveryone);
      setSelectedTarget(everyone ? { type: 'role', id: everyone.id } : { type: targets[0].type, id: targets[0].id });
    }
  }, [targets, selectedTarget]);

  const currentOverwrite = overwrites.find(
    (overwrite) => overwrite.target_type === selectedTarget?.type && overwrite.target_id === selectedTarget?.id,
  ) || { allow: 0, deny: 0 };

  const stateFor = (flag: number) => {
    if ((currentOverwrite.allow & flag) === flag) return 'allow';
    if ((currentOverwrite.deny & flag) === flag) return 'deny';
    return 'neutral';
  };

  const setStateFor = async (flag: number, state: 'deny' | 'neutral' | 'allow') => {
    if (!selectedTarget) return;
    let allow = currentOverwrite.allow || 0;
    let deny = currentOverwrite.deny || 0;
    allow &= ~flag;
    deny &= ~flag;
    if (state === 'allow') allow |= flag;
    if (state === 'deny') deny |= flag;
    const next = { target_type: selectedTarget.type, target_id: selectedTarget.id, allow, deny };
    setOverwrites((prev) => [
      ...prev.filter((overwrite) => !(overwrite.target_type === selectedTarget.type && overwrite.target_id === selectedTarget.id)),
      next,
    ]);
    await axios.put(`${import.meta.env.VITE_API_URL}/channels/${channel.id}/permissions`, next, {
      headers: { Authorization: `Bearer ${token}` },
    });
  };

  const saveOverview = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${import.meta.env.VITE_API_URL}/channels/${channel.id}`,
        { name, description },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (currentServer?.id) await fetchServerChannels(currentServer.id);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-3xl max-h-[86vh] overflow-hidden rounded-xl border border-border-color bg-bg-secondary shadow-2xl">
        <header className="h-14 px-5 border-b border-border-color flex items-center justify-between">
          <div className="flex items-center gap-2">
            {channel.type === 'category' ? <Folder className="w-4 h-4 text-text-muted" /> : channel.type === 'voice' ? <Lock className="w-4 h-4 text-text-muted" /> : <Hash className="w-4 h-4 text-text-muted" />}
            <h2 className="text-sm font-bold text-text-primary">Edit {channel.type === 'category' ? 'Category' : 'Channel'}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-text-muted hover:text-text-primary hover:bg-highlight">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="grid grid-cols-[180px_1fr] min-h-[520px]">
          <aside className="border-r border-border-color p-3 bg-bg-primary/40">
            {(['overview', 'permissions'] as const).map((item) => (
              <button
                key={item}
                onClick={() => setTab(item)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
                  tab === item ? 'bg-highlight text-text-primary' : 'text-text-secondary hover:bg-highlight/40 hover:text-text-primary'
                }`}
              >
                {item}
              </button>
            ))}
          </aside>

          <main className="p-5 overflow-y-auto">
            {tab === 'overview' ? (
              <div className="space-y-4">
                {channel.parent_id && (
                  <div className={`rounded-lg border px-3 py-2 text-xs font-semibold ${
                    channel.permission_sync_state === 'synced'
                      ? 'border-success/30 bg-success/10 text-success'
                      : 'border-warning/30 bg-warning/10 text-warning'
                  }`}>
                    {channel.permission_sync_state === 'synced' ? 'Synced with category' : 'Out of sync with category'}
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-text-secondary mb-1.5">Channel Name</label>
                  <input value={name} onChange={(event) => setName(event.target.value)} className="settings-input" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-text-secondary mb-1.5">Description</label>
                  <textarea value={description} onChange={(event) => setDescription(event.target.value)} className="settings-input min-h-24 resize-none" />
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={saveOverview} disabled={saving || !name.trim()} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent-color text-black text-xs font-bold disabled:opacity-50">
                    <Save className="w-4 h-4" />
                    Save Changes
                  </button>
                  {channel.parent_id && channel.permission_sync_state === 'out_of_sync' && (
                    <button
                      onClick={async () => {
                        await syncChannelPermissions(channel.id);
                        if (currentServer?.id) await fetchServerChannels(currentServer.id);
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border-color bg-bg-tertiary text-text-primary text-xs font-bold hover:bg-highlight"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Sync Permissions
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
              {channel.type === 'category' && (
                <div className="mb-4 rounded-lg border border-border-color bg-bg-tertiary/50 px-3 py-2 text-xs text-text-secondary">
                  Category permission changes apply to channels that are still synced with this category.
                </div>
              )}
              {channel.parent_id && channel.permission_sync_state === 'synced' && (
                <div className="mb-4 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
                  Editing these permissions will make this channel out of sync with its category.
                </div>
              )}
              <div className="grid grid-cols-[220px_1fr] gap-4">
                <div className="space-y-1">
                  {targets.map((target) => (
                    <button
                      key={`${target.type}:${target.id}`}
                      onClick={() => setSelectedTarget({ type: target.type, id: target.id })}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                        selectedTarget?.type === target.type && selectedTarget.id === target.id
                          ? 'bg-highlight text-text-primary'
                          : 'text-text-secondary hover:bg-highlight/40'
                      }`}
                    >
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: target.color || (target.type === 'member' ? '#f59e0b' : '#99aab5') }} />
                      <span className="truncate">{target.label}{target.bot ? ' · App' : ''}</span>
                    </button>
                  ))}
                </div>

                <div className="space-y-2">
                  {PERMS.map((perm) => {
                    const current = stateFor(perm.flag);
                    return (
                      <div key={perm.flag} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-bg-tertiary/50">
                        <span className="text-sm font-medium text-text-primary">{perm.label}</span>
                        <div className="flex rounded-lg overflow-hidden border border-border-color">
                          {(['deny', 'neutral', 'allow'] as const).map((state) => (
                            <button
                              key={state}
                              onClick={() => setStateFor(perm.flag, state)}
                              className={`px-3 py-1.5 text-xs font-bold capitalize transition-colors ${
                                current === state
                                  ? state === 'allow'
                                    ? 'bg-success text-black'
                                    : state === 'deny'
                                      ? 'bg-error text-white'
                                      : 'bg-highlight text-text-primary'
                                  : 'bg-bg-primary text-text-muted hover:text-text-primary'
                              }`}
                            >
                              {state}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default EditChannelModal;
